import { Head, useForm } from '@inertiajs/react'
import { useState, type FormEvent } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { InputField } from '@/components/shared/form'

interface LoginProps {
  flash?: Record<string, string>
  errors?: Record<string, string[]>
}

interface LoginValues {
  email: string
  password: string
}

export default function Login({ flash, errors }: LoginProps) {
  const [showPassword, setShowPassword] = useState(false)
  const form = useForm<LoginValues>({ email: '', password: '' })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    form.post('/login')
  }

  const baseError = errors?.base?.[0] || flash?.alert

  return (
    <>
      <Head title="Sign in" />

      <div className="bg-bg-subtle flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="mb-5 flex items-center gap-2.5">
            <span
              aria-hidden
              className="bg-fg text-2xs text-bg grid size-7 shrink-0 place-items-center rounded-md font-semibold"
            >
              RH
            </span>
            <span className="text-fg text-sm font-semibold tracking-tight">Roundhouse</span>
          </div>

          <Card className="px-5 py-5">
            <h1 className="text-fg m-0 text-xl font-semibold">Sign in</h1>
            <p className="text-fg-muted mt-1 mb-5 text-sm">
              Enter your credentials to reach the console.
            </p>

            {/* A whole-form failure is a banner, not a field error — it stays
                until the state changes, which is exactly what <Alert> is for. */}
            {baseError && (
              <Alert dot variant="danger" className="mb-4 rounded-md">
                {baseError}
              </Alert>
            )}

            <form onSubmit={submit} noValidate className="flex flex-col gap-3">
              <InputField
                form={form}
                name="email"
                label="Email"
                type="email"
                inputMode="email"
                autoComplete="email"
                spellCheck={false}
                required
              />

              <InputField
                form={form}
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                trailing={
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="text-fg-subtle hover:text-fg focus-visible:outline-accent flex size-full items-center justify-center rounded-md transition-colors duration-100 ease-out outline-none focus-visible:outline-2 focus-visible:-outline-offset-2"
                  >
                    {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                }
              />

              {/* Stays enabled until the request actually starts. */}
              <Button type="submit" size="lg" disabled={form.processing} className="mt-1 w-full">
                {form.processing ? 'Signing in…' : 'Sign In'}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </>
  )
}
